/**
 * THE VIDEO OF A VIDEO DRAFT, AS THE EDITOR KNOWS IT (v1.0.29), and the reads that keep it current.
 *
 * The working copy carries the video without addresses; `getArticleVideo` adds a signed player
 * address and the cover frames once an upload is `ready`. So the screen reads it once on opening,
 * then:
 *   - while the media worker has it (`uploaded`, `processing`), again every `pollMs`;
 *   - once it plays, again before its addresses run out (they last about an hour), and at once when
 *     the player says it could not load them (`refresh`);
 *   - after a refused read, again after a wait that doubles from a poll with each failure in a row,
 *     up to a minute - a dropped connection must not stop the wait, nor a dead one be asked every
 *     five seconds for ever. From the third failure in a row the screen says so with a retry
 *     (`readFailure`, `readAgain`) in place of its spinner; any answer clears it.
 * Each new answer re-arms exactly one timer, so nothing piles up; leaving the screen stops it.
 *
 * ★ THE PLAYER'S "AGAIN AT ONCE" IS BOUNDED. Every read signs a new address, so a file that will
 * not play at all would fail, be read, and fail again for ever. So the player is given a new
 * address at most `PLAYER_RETRIES` times while the screen is open, and not twice within
 * `PLAYER_RETRY_GAP_MS`; a failure past either says the video does not play (`playbackFailed`).
 * A timer measures the gap - the app reads no clock.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useEditorApi } from '../../features/cabinet/api/editor-api';
import { NotFoundFailure, isApiProblem } from '../../features/cabinet/api/problem';
import type { ArticleVideo } from '../../features/cabinet/api/editor-api';

/** How often a video the media worker still has is asked about. */
export const VIDEO_POLL_MS = 5_000;

/** The longest wait between two reads after failures in a row. */
export const VIDEO_READ_BACKOFF_CAP_MS = 60_000;

/** How many failed reads in a row before the screen says so. */
export const VIDEO_READ_FAILURES_SHOWN = 3;

/** Addresses are good for about an hour; they are renewed well inside it. */
const RENEW_ADDRESSES_MS = 45 * 60_000;

/** How many times a failing player is given a new address while the screen is open. */
export const PLAYER_RETRIES = 3;

/** The least time between two of those. */
export const PLAYER_RETRY_GAP_MS = 2 * 60_000;

export interface ArticleVideoState {
  /** Null when the draft has no video. */
  readonly video: ArticleVideo | null;
  /** True until the first read has answered with the video's addresses. */
  readonly reading: boolean;
  /** The last error once reads have failed `VIDEO_READ_FAILURES_SHOWN` times in a row, else null. */
  readonly readFailure: { readonly error: unknown } | null;
  /** Read again now, after `readFailure`; until it answers the screen waits as before. */
  readonly readAgain: () => void;
  /** What a set or a remove answered: the screen takes it, then reads it again with addresses. */
  readonly adopt: (video: ArticleVideo | null) => void;
  /**
   * The player could not load its address, which has most likely run out: read again now, within
   * the bounds above - past them, `playbackFailed`.
   */
  readonly refresh: () => void;
  /** The player failed past its retries: the screen says the video does not play just now. */
  readonly playbackFailed: boolean;
}

function isGone(error: unknown): boolean {
  return error instanceof NotFoundFailure || (isApiProblem(error) && error.status === 404);
}

function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

export function useArticleVideo(
  articleId: string,
  initial: ArticleVideo | null,
  pollMs: number = VIDEO_POLL_MS,
  retryGapMs: number = PLAYER_RETRY_GAP_MS,
): ArticleVideoState {
  const api = useEditorApi();
  const [video, setVideo] = useState<ArticleVideo | null>(initial);
  // Whether `video` came from `getArticleVideo` (with addresses), rather than from a write.
  const [fresh, setFresh] = useState(false);
  const [failedReads, setFailedReads] = useState(0);
  const [lastError, setLastError] = useState<unknown>(undefined);
  // `readAgain` was pressed and its read has not answered: it goes at once, whatever the backoff.
  const [againNow, setAgainNow] = useState(false);
  const [asked, setAsked] = useState(0);

  useEffect(() => {
    if (video === null) {
      return undefined;
    }
    const withWorker = video.state === 'uploaded' || video.state === 'processing';
    const playable = video.source === 'upload' && video.state === 'ready';
    const delay = !fresh
      ? failedReads === 0 || againNow
        ? 0
        : Math.min(pollMs * 2 ** (failedReads - 1), VIDEO_READ_BACKOFF_CAP_MS)
      : withWorker
        ? pollMs
        : playable
          ? RENEW_ADDRESSES_MS
          : null;
    if (delay === null) {
      return undefined;
    }
    const controller = new AbortController();
    const timer = setTimeout(() => {
      api
        .getArticleVideo(articleId, controller.signal)
        .then((next) => {
          if (controller.signal.aborted) {
            return;
          }
          setVideo(next);
          setFresh(true);
          setFailedReads(0);
          setAgainNow(false);
        })
        .catch((cause: unknown) => {
          if (controller.signal.aborted || isAbort(cause)) {
            return;
          }
          setAgainNow(false);
          if (isGone(cause)) {
            setVideo(null);
            return;
          }
          setFresh(false);
          setFailedReads((count) => count + 1);
          setLastError(cause);
        });
    }, delay);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [api, articleId, video, fresh, failedReads, againNow, asked, pollMs]);

  const readAgain = useCallback(() => {
    setAgainNow(true);
  }, []);

  const [playbackFailed, setPlaybackFailed] = useState(false);
  const retries = useRef(0);
  const resting = useRef<ReturnType<typeof setTimeout> | null>(null);

  const adopt = useCallback((next: ArticleVideo | null) => {
    setVideo(next);
    setFresh(false);
    setFailedReads(0);
    setAgainNow(false);
    // Another video, or none: the player's retries start again.
    retries.current = 0;
    setPlaybackFailed(false);
  }, []);

  useEffect(
    () => () => {
      if (resting.current !== null) {
        clearTimeout(resting.current);
      }
    },
    [],
  );

  const refresh = useCallback(() => {
    if (retries.current >= PLAYER_RETRIES || resting.current !== null) {
      setPlaybackFailed(true);
      return;
    }
    retries.current += 1;
    resting.current = setTimeout(() => {
      resting.current = null;
    }, retryGapMs);
    setPlaybackFailed(false);
    setFresh(false);
    setAsked((count) => count + 1);
  }, [retryGapMs]);

  const readFailure =
    failedReads >= VIDEO_READ_FAILURES_SHOWN && !againNow ? { error: lastError } : null;

  return {
    video,
    reading: video !== null && !fresh,
    readFailure,
    readAgain,
    adopt,
    refresh,
    playbackFailed,
  };
}
